import { GridCellProps } from '@progress/kendo-react-grid';
import { MouseEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import GridCellBase from 'src/components/GridCellBase';
import Button from 'src/components/design-system/Button';
import useConfirm from 'src/hooks/useConfirm';
import useRedirect from 'src/hooks/useRedirect';
import { usePunchoutRedirectContext } from 'src/sections/punchout_order_guide/PunchoutRedirectContext';
import useCreateBlankPoMutation from 'src/services/manage_suppliers/useCreateBlankPoMutation';
import { ManageSupplier } from 'src/types/manage-suppliers/ManageSupplier';
import { stopPropagation } from 'src/utils';

const ActionCell = (props: GridCellProps) => {
  const { t } = useTranslation();
  const { confirm } = useConfirm();
  const { getUrl, redirect } = useRedirect();
  const { mutateAsync: createBlankPo, isPending, variables } = useCreateBlankPoMutation();
  const { openPunchout, isOpeningPunchout, openingCatalogId } = usePunchoutRedirectContext();
  const dataItem = props.dataItem as ManageSupplier;

  const handleCreatePo = useCallback(
    async (event: MouseEvent<HTMLButtonElement>) => {
      stopPropagation(event);

      const isConfirmed = await confirm({
        title: t('Confirm PO Creation'),
        content: t('Create an Offline PO for the selected supplier?'),
        confirmButtonText: t('OK'),
        cancelButtonText: t('Cancel'),
      });

      if (!isConfirmed) {
        return;
      }

      try {
        const poId = await createBlankPo({
          supplierId: dataItem.supplierClientIdCombined,
          isOfflineSupplier: dataItem.isOfflineSupplier,
        });

        if (!poId) {
          toast.error(t('Unable to create the purchase order. Please try again.'));
          return;
        }

        redirect(getUrl('poDetails')(String(poId)), true, false);
      } catch {
        toast.error(t('Unable to create the purchase order. Please try again.'));
      }
    },
    [
      confirm,
      createBlankPo,
      dataItem.isOfflineSupplier,
      dataItem.supplierClientIdCombined,
      getUrl,
      redirect,
      t,
    ]
  );

  const handleOpenCatalog = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      stopPropagation(event);
      redirect(getUrl('viewProductList')(String(dataItem.catalogId)), false, false);
    },
    [dataItem.catalogId, getUrl, redirect]
  );

  const handleOpenPunchout = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      stopPropagation(event);
      openPunchout(dataItem.catalogId);
    },
    [dataItem.catalogId, openPunchout]
  );

  if (!dataItem.actionType) {
    return (
      <GridCellBase {...props} disableToolTip>
        {null}
      </GridCellBase>
    );
  }

  const actionLabels: Record<NonNullable<ManageSupplier['actionType']>, string> = {
    createPo: t('Create Offline PO'),
    openCatalog: t('Open Catalog'),
    openPunchout: t('Open Punchout'),
  };

  const onClick =
    dataItem.actionType === 'createPo'
      ? handleCreatePo
      : dataItem.actionType === 'openPunchout'
      ? handleOpenPunchout
      : handleOpenCatalog;
  const isLoading =
    (isPending && variables?.supplierId === dataItem.supplierClientIdCombined) ||
    (isOpeningPunchout && openingCatalogId === dataItem.catalogId);

  return (
    <GridCellBase {...props} disableToolTip>
      <div className="flex h-full w-full items-center justify-center px-2">
        <Button
          type="button"
          color={dataItem.actionType === 'createPo' ? 'primary' : 'info'}
          onClick={onClick}
          loading={isLoading}
          size="sm"
          className="supplier-action-btn w-[118px] !justify-center text-[12px]"
        >
          {actionLabels[dataItem.actionType]}
        </Button>
      </div>
    </GridCellBase>
  );
};

export default ActionCell;
